import { ColumnDef, ExpandedState, getCoreRowModel, getExpandedRowModel, getFacetedUniqueValues, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, Row, RowSelectionState, Updater, useReactTable } from '@tanstack/react-table';
import { useState } from 'react';
import { useUser } from '../../../api/queries/use-user';
import { Load } from '../../../types';
import { ArbitraryTextFilter } from '../table-components/filters/arbitrary-text-filter';
import { ComparisonFilter } from '../table-components/filters/comparison-filter';
import { CustomerFilter } from '../table-components/filters/customer-filter';
import { DriverPropertiesFilter } from '../table-components/filters/driver-properties-filter';
import { LocationGroupFilter } from '../table-components/filters/location-group-filter';
import { MedicationFilter } from '../table-components/filters/medication-filter';
import { getPageSize } from '../utils/drafts-loads-utils';
import { FIRST_STOP_COLUMN } from './use-draft-load-columns';
import { useDraftLoadPreferences } from './useDraftLoadPreferences';

export type GlobalFilterItemType = 'arbitraryTextFilter' | 'comparisonFilter' | 'customerFilter' | 'driverPropertiesFilter' | 'locationGroupFilter' | 'medicationFilter' | 'selectedFilter';

export type GlobalFilterObject<T extends GlobalFilterItemType, U> = {
    type: T;
    label: string;
    value: U;
    filterFn: (row: Row<Load>, value: U) => boolean;
}

export type GlobalFilterItem = ArbitraryTextFilter | ComparisonFilter | CustomerFilter | DriverPropertiesFilter | LocationGroupFilter | MedicationFilter | { type: 'selectedFilter' };

// tanstack's doesn't seem to give implementation control over preventing globalFilter from being undefined
export type GlobalFilter = GlobalFilterItem[] | undefined;

// getSortedRowModel - https://github.com/TanStack/table/issues/4888
// provided getSortedRowModel function from @tanstack/react-table causes 2 renders when sorting
// spent quite a lot of time trying to write our own, but determined probably more work then it's worth at this time

export const useDraftLoadTable = (
    isDrafts: boolean,
    columns: ColumnDef<Load, any>[],
    loads: Load[],
) => {
    const { user } = useUser();
    const { draftsLoadsDefaultSort } = useDraftLoadPreferences();
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
    const [rowSelectionOrder, setRowSelectionOrder] = useState<number[]>([]);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: getPageSize(isDrafts, user),
    });

    function globalFilterFn(row: Row<Load>, _: string, globalFilter: GlobalFilter) {
        if (globalFilter && globalFilter.find(item => item.type === 'selectedFilter') && !row.getIsSelected()) {
            return false;
        }

        const filterItemsByTypeMap = globalFilter?.reduce((acc: Record<string, GlobalFilterItem[]>, filterItem) => {
            if (filterItem.type !== 'selectedFilter') {
                if (!acc[filterItem.type]) {
                    acc[filterItem.type] = [];
                }
                acc[filterItem.type].push(filterItem);
            }
            return acc;
        }, {});

        for (const filterType in filterItemsByTypeMap) {
            if (filterType === 'driverPropertiesFilter') {
                const filterTypeEveryResult = filterItemsByTypeMap[filterType].every((filterItem: GlobalFilterItem) => {
                    // @ts-ignore - not sure why second argument for filterFn is typed as 'never'
                    return filterItem.filterFn(row, filterItem.value);
                });
                if (!filterTypeEveryResult) {
                    return false;
                }
            } else {
                const filterTypeSomeResult = filterItemsByTypeMap[filterType].some((filterItem: GlobalFilterItem) => {
                    // @ts-ignore - not sure why second argument for filterFn is typed as 'never'
                    return filterItem.filterFn(row, filterItem.value);
                });
                if (!filterTypeSomeResult) {
                    return false;
                }
            }
        }
        return true;
    }

    const onPaginationChange = (updater: Updater<PaginationState>) => {
        if (typeof updater === 'function') {
            const newPagination = updater(pagination);
            setPagination(newPagination);
        }
    }

    const onSelectionChange = (updater: Updater<RowSelectionState>) => {
        if (typeof updater === 'function') {
            const newSelection = updater(rowSelection);
            setRowSelection(newSelection);

            const newSelectionKeys = Object.keys(newSelection);

            if (newSelectionKeys.length) {
                const shouldRemove = newSelectionKeys.every(x => rowSelectionOrder.includes(parseInt(x)));

                if (shouldRemove) {
                    const newRowSelectionOrder = rowSelectionOrder.filter(x => newSelectionKeys.includes(x.toString()));

                    setRowSelectionOrder(newRowSelectionOrder);
                } else {
                    const added = newSelectionKeys.filter(x => !rowSelectionOrder.includes(parseInt(x)));
                    const mappedAdded = added.map(x => parseInt(x));
                    const newRowSelectionOrder = [
                        ...rowSelectionOrder,
                        ...mappedAdded,
                    ];

                    setRowSelectionOrder(newRowSelectionOrder);
                }
            } else {
                setRowSelectionOrder([]);
            }
        } else {
            // when calling table.resetRowSelection(true);
            // updater is empty object and also needs to reset rowSelectionOrder
            setRowSelection(updater);
            setRowSelectionOrder([]);
        }
    }

    const table = useReactTable({
        data: loads,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(), // this enables client side pagination
        // read above for performance info on getSortedRowModel
        getSortedRowModel: getSortedRowModel(),

        // FILTERS
        // these do not work like you would expect.  A gloabl filter from what I can tell in react-table means
        // run this filter on each COLUMN you tell me to. While what we are after is, here is a list of rules
        // for each row, check each row ONCE for all these things, if any exists, return true for that row, no need
        // to run the logic on each column (ignoring the fact that there are some crazy rules determining 'can-filter' based on data-type of the data)
        // https://tanstack.com/table/latest/docs/api/features/global-filtering#can-filter
        // because of that all columns are set enableGlobalFilter: false. Except STATUS_COLUMN which will fire off the global filter
        // how I would execpt a gloabl filter to run once per row.
        getFilteredRowModel: getFilteredRowModel(),
        globalFilterFn,

        getFacetedUniqueValues: getFacetedUniqueValues(),
        getExpandedRowModel: getExpandedRowModel(),
        onRowSelectionChange: onSelectionChange,
        // getRowId - used to keep track of load ids when selecting loads and new loads get added to the page (refresh)
        getRowId: (row) => row.id.toString(),
        initialState: {
            sorting: [{
                id: FIRST_STOP_COLUMN,
                desc: draftsLoadsDefaultSort,
            }],
            rowSelection: rowSelection,
        },
        rowCount: loads.length,
        enableSortingRemoval: false,
        // if this is not set, it must use datatype of accessor to determine default sort?
        // might be asc or desc depending
        sortDescFirst: false,
        state: {
            pagination: pagination,
            rowSelection: rowSelection,
            expanded: expanded,
        },
        onExpandedChange: setExpanded,
        onPaginationChange: onPaginationChange,
    });

    return {
        table: table,
        rowSelectionOrder: rowSelectionOrder,
    };
}
